import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Webcam from "react-webcam";
import { Loader, Text, useMantineTheme } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { isMobile } from "react-device-detect";
import useFaceDetection from "../../../hooks/useFaceDetection";
import { collectCameraSignals } from "../../../utils/captureIntegrity";
import useLocale from "../../../hooks/useLocale";
import { ReactComponent as VeratadLogo } from "../../../assets/veratad_ai_white_logo_large.svg";

const STYLE_ID = "veratad-face-capture-styles";

function injectStyles(glowColor) {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    :root { --glow-color: ${glowColor}; }

    @keyframes glowPulse {
      0%, 100% {
        box-shadow:
          0 0 calc(6px * var(--glow-intensity)) calc(2px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(18px * var(--glow-intensity)) calc(5px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(35px * var(--glow-intensity)) calc(10px * var(--glow-intensity)) var(--glow-color);
        opacity: calc(0.3 + 0.14 * var(--glow-intensity));
      }
      50% {
        box-shadow:
          0 0 calc(12px * var(--glow-intensity)) calc(4px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(30px * var(--glow-intensity)) calc(10px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(60px * var(--glow-intensity)) calc(18px * var(--glow-intensity)) var(--glow-color);
        opacity: calc(0.4 + 0.12 * var(--glow-intensity));
      }
    }

    @keyframes glowLocked {
      0% {
        box-shadow:
          0 0 calc(14px * var(--glow-intensity)) calc(4px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(35px * var(--glow-intensity)) calc(10px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(60px * var(--glow-intensity)) calc(18px * var(--glow-intensity)) var(--glow-color);
      }
      100% {
        box-shadow:
          0 0 calc(10px * var(--glow-intensity)) calc(3px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(25px * var(--glow-intensity)) calc(8px * var(--glow-intensity)) var(--glow-color),
          0 0 calc(45px * var(--glow-intensity)) calc(14px * var(--glow-intensity)) var(--glow-color);
      }
    }
  `;
  document.head.appendChild(style);
}

function removeStyles() {
  const el = document.getElementById(STYLE_ID);
  if (el) el.remove();
}

export default function VeratadCapture({ onCapture, onError, onReady, onCameraAllowed }) {
  const theme = useMantineTheme();
  const locale = useLocale("face-capture");
  const { width: vw, height: vh } = useViewportSize();
  const webcamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);

  const primaryColor =
    theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5];

  const {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    setOnAutoCapture,
    getFrameChain,
  } = useFaceDetection(webcamRef, cameraReady);

  const feedbackMessage = locale?.feedback?.[feedbackKey] || feedbackKey;

  // Count how many gates are passing (0-5) to drive glow intensity
  const passingCount = Object.values(gatingState).filter(Boolean).length;

  // Oval sizing
  const rx = isMobile ? Math.min(vw * 0.38, 170) : 160;
  const ry = isMobile ? Math.min(vh * 0.26, 230) : 215;
  const cx = vw / 2;
  const cy = vh * 0.40;

  // Inject/remove CSS
  useEffect(() => {
    injectStyles(primaryColor);
    return removeStyles;
  }, [primaryColor]);

  // Notify parent when models + camera are ready, passing camera signals
  useEffect(() => {
    if (modelsLoaded && cameraReady && onReady) {
      onReady(cameraSignalsRef.current);
    }
  }, [modelsLoaded, cameraReady, onReady]);

  // Propagate model load errors to parent
  useEffect(() => {
    if (modelLoadError && onError) {
      onError({ type: "model_load_error", message: modelLoadError?.message || "Failed to load face detection models" });
    }
  }, [modelLoadError, onError]);

  const handleCapture = useCallback(() => {
    if (!webcamRef.current) return;
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc && onCapture) {
        onCapture(imageSrc, getFrameChain());
      }
    } catch (err) {
      if (onError) onError({ type: "capture_error", message: err?.message || "Screenshot capture failed" });
    }
  }, [onCapture, onError, getFrameChain]);

  // Register auto-capture callback
  useEffect(() => {
    setOnAutoCapture(handleCapture);
  }, [handleCapture, setOnAutoCapture]);

  const cameraSignalsRef = useRef(null);

  const handleUserMedia = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video) return;
    if (onCameraAllowed) onCameraAllowed();
    const checkReady = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
        // Collect camera signals once the track is active — virtual cameras
        // won't report facingMode, which the server can flag
        cameraSignalsRef.current = collectCameraSignals(video);
        setCameraReady(true);
      } else {
        requestAnimationFrame(checkReady);
      }
    };
    requestAnimationFrame(checkReady);
  }, [onCameraAllowed]);

  const handleUserMediaError = useCallback(
    (err) => {
      if (onError) onError({ type: "camera_error", name: err?.name || "UnknownError", message: err?.message || "Camera access failed" });
    },
    [onError]
  );

  const portal = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        backgroundColor: "#000",
        overflow: "hidden",
      }}
    >
      {/* Camera setup indicator — centered in oval */}
      {(!cameraReady || !modelsLoaded) && (
        <div
          style={{
            position: "absolute",
            left: cx - rx,
            top: cy - ry,
            width: rx * 2,
            height: ry * 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "none",
            zIndex: 15,
          }}
        >
          <Loader size={36} color="white" />
          <Text
            fw={500}
            c="white"
            fz={14}
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}
          >
            {locale?.capture?.settingUp || "Setting up your camera..."}
          </Text>
        </div>
      )}

      {/* Webcam layer */}
      <Webcam
        ref={webcamRef}
        audio={false}
        screenshotFormat="image/jpeg"
        screenshotQuality={0.92}
        videoConstraints={{
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 16 / 9 },
        }}
        mirrored
        onUserMedia={handleUserMedia}
        onUserMediaError={handleUserMediaError}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: cameraReady && modelsLoaded ? 1 : 0,
          transition: "opacity 0.3s ease-in",
        }}
      />

      {/* SVG overlay with oval cutout */}
      <svg
        width={vw}
        height={vh}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        <defs>
          <mask id="face-oval-mask">
            <rect width="100%" height="100%" fill="white" />
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="black" />
          </mask>
        </defs>

        {/* Dark overlay with oval cutout */}
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.85)"
          mask="url(#face-oval-mask)"
        />

        {/* Oval border — stroke only, glow handled by HTML div below */}
        <ellipse
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          fill="none"
          stroke={passingCount === 0 ? "rgba(255,255,255,0.5)" : primaryColor}
          strokeWidth={isAllPassing ? 5 : passingCount >= 3 ? 4.5 : 3}
          style={{ transition: "stroke 0.4s, stroke-width 0.4s" }}
        />
      </svg>

      {/* Glow div — uses box-shadow instead of SVG drop-shadow for WebKit compat */}
      {(() => {
        const glowIntensity = passingCount === 0 ? 1 : passingCount + 1;
        const glowColorValue = passingCount === 0 ? "rgba(255,255,255,0.4)" : primaryColor;
        return (
          <div
            style={{
              position: "absolute",
              left: cx - rx,
              top: cy - ry,
              width: rx * 2,
              height: ry * 2,
              borderRadius: "50%",
              background: "transparent",
              pointerEvents: "none",
              zIndex: 10,
              '--glow-intensity': glowIntensity,
              '--glow-color': glowColorValue,
              animation: isAllPassing
                ? "glowLocked 1.5s ease-in-out infinite alternate"
                : "glowPulse 2s ease-in-out infinite",
              transition: "opacity 0.4s",
            }}
          />
        );
      })()}

      {/* Feedback text */}
      <div
        style={{
          position: "absolute",
          top: cy + ry + 30,
          left: 0,
          width: "100%",
          textAlign: "center",
          pointerEvents: "none",
          zIndex: 20,
        }}
      >
        <span
          style={{
            color: "#fff",
            fontSize: isMobile ? 16 : 18,
            fontWeight: 500,
            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
          }}
        >
          {cameraReady && modelsLoaded ? feedbackMessage : ""}
        </span>
      </div>

      {/* Branding */}
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? 54 : 32,
          left: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          pointerEvents: "none",
          zIndex: 20,
        }}
      >
        <span
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          {locale?.capture?.capturedBy || "Captured by"}
        </span>
        <VeratadLogo style={{ height: 28, width: "auto", opacity: 0.9 }} />
      </div>
    </div>
  );

  return ReactDOM.createPortal(portal, document.body);
}
